import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiDownload, FiUpload, FiRefreshCcw, FiCheckCircle, FiRotateCcw } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import productBulkUpdateService from '../services/productBulkUpdateService';

const SCOPE_OPTIONS = [
  { value: 'PRODUCT_MASTER_ONLY', label: 'Product master only' },
  { value: 'SELECTED_STORES_ONLY', label: 'Selected stores only (price override + store cost)' },
  { value: 'PRODUCT_MASTER_AND_SELECTED_STORES', label: 'Product master + selected stores' },
];

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const ProductBulkUpdatesPage = () => {
  const { user } = useAuth();
  const { userStores } = useStore();
  const [scopeMode, setScopeMode] = useState('PRODUCT_MASTER_ONLY');
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [file, setFile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [applyingJobId, setApplyingJobId] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const canUse = user?.permissions?.includes('products:bulk_update');
  const includesStores = scopeMode === 'SELECTED_STORES_ONLY' || scopeMode === 'PRODUCT_MASTER_AND_SELECTED_STORES';

  const canValidateJob = useCallback(
    (job) => job?.status === 'uploaded' || (job?.status === 'failed' && !job?.hasRollbackData),
    []
  );

  const canApplyJob = useCallback((job) => job?.status === 'validated', []);

  const storeOptions = useMemo(() => {
    if (!Array.isArray(userStores)) return [];
    return userStores
      .filter((store) => store?._id && store?.name)
      .map((store) => ({ id: store._id, name: store.name }));
  }, [userStores]);

  useEffect(() => {
    if (!includesStores) {
      setSelectedStoreIds([]);
    }
  }, [includesStores]);

  const loadJobs = useCallback(async () => {
    if (!canUse) return;
    try {
      setLoadingJobs(true);
      const rows = await productBulkUpdateService.listJobs(25);
      setJobs(rows);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load jobs.');
    } finally {
      setLoadingJobs(false);
    }
  }, [canUse]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const handleDownloadTemplate = async () => {
    try {
      setError('');
      const blob = await productBulkUpdateService.downloadTemplate();
      saveBlob(blob, 'product-bulk-update-template.xlsx');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to download template.');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please choose a file to upload.');
      return;
    }
    if (includesStores && selectedStoreIds.length === 0) {
      setError('Please select at least one store for the selected scope.');
      return;
    }
    try {
      setUploading(true);
      setError('');
      setInfo('');
      await productBulkUpdateService.uploadJob({
        file,
        pricingScopeMode: scopeMode,
        storeIds: selectedStoreIds,
      });
      setFile(null);
      setInfo('Upload successful. Run validation before applying.');
      await loadJobs();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload job.');
    } finally {
      setUploading(false);
    }
  };

  const handleValidate = async (job) => {
    try {
      setError('');
      setInfo('');
      const result = await productBulkUpdateService.validateJob(job._id);
      const summary = result?.job?.validationSummary;
      setInfo(
        `Validated: total=${summary?.totalRows || 0}, valid=${summary?.validRows || 0}, invalid=${summary?.invalidRows || 0}`
      );
      await loadJobs();
    } catch (err) {
      setError(err.response?.data?.message || 'Validation failed.');
    }
  };

  const handleApply = async (job) => {
    if (!window.confirm('Apply this bulk update job now?')) return;
    try {
      setApplyingJobId(job._id);
      setError('');
      setInfo('');
      await productBulkUpdateService.applyJob(job._id);
      setInfo('Bulk update applied successfully.');
      await loadJobs();
    } catch (err) {
      setError(err.response?.data?.message || 'Apply failed.');
    } finally {
      setApplyingJobId('');
    }
  };

  const handleRollback = async (job) => {
    if (!window.confirm('Rollback this bulk update job and restore previous values?')) return;
    try {
      setError('');
      setInfo('');
      await productBulkUpdateService.rollbackJob(job._id);
      setInfo('Rollback completed.');
      await loadJobs();
    } catch (err) {
      setError(err.response?.data?.message || 'Rollback failed.');
    }
  };

  const handleDownloadValidationReport = async (job) => {
    try {
      const blob = await productBulkUpdateService.downloadValidationReport(job._id);
      saveBlob(blob, `bulk-update-validation-${job._id}.csv`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to download validation report.');
    }
  };

  if (!canUse) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
        <p className="mt-2">You do not have permission to access Product Bulk Updates.</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Product Bulk Updates</h1>
          <p className="text-gray-500">Template-driven upload with validate, apply and rollback.</p>
        </div>
        <button
          type="button"
          onClick={loadJobs}
          className="inline-flex items-center px-4 py-2 bg-white border rounded-md shadow-sm hover:bg-gray-50"
        >
          <FiRefreshCcw className="mr-2" />
          Refresh Jobs
        </button>
      </div>

      {error && <p className="mb-4 bg-red-100 text-red-700 p-3 rounded">{error}</p>}
      {info && <p className="mb-4 bg-green-100 text-green-700 p-3 rounded">{info}</p>}

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-semibold mb-4">Create Job</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pricing Scope</label>
            <select
              value={scopeMode}
              onChange={(e) => setScopeMode(e.target.value)}
              className="w-full border rounded-md px-3 py-2"
            >
              {SCOPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              <FiDownload className="mr-2" />
              Download Template
            </button>
          </div>
        </div>

        {includesStores && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Target Stores</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-auto border rounded-md p-3">
              {storeOptions.map((store) => (
                <label key={store.id} className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedStoreIds.includes(store.id)}
                    onChange={(e) => {
                      setSelectedStoreIds((prev) =>
                        e.target.checked
                          ? [...prev, store.id]
                          : prev.filter((storeId) => storeId !== store.id)
                      );
                    }}
                    className="h-4 w-4"
                  />
                  {store.name}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-col md:flex-row md:items-center gap-3">
          <input
            type="file"
            accept=".csv,.xlsx"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full md:w-auto"
          />
          <button
            type="button"
            disabled={uploading}
            onClick={handleUpload}
            className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
          >
            <FiUpload className="mr-2" />
            {uploading ? 'Uploading...' : 'Upload Job'}
          </button>
          {file && <span className="text-sm text-gray-600">{file.name}</span>}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Created</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">File</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Scope</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Validation</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loadingJobs && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                  Loading jobs...
                </td>
              </tr>
            )}
            {!loadingJobs && jobs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                  No jobs found.
                </td>
              </tr>
            )}
            {!loadingJobs &&
              jobs.map((job) => {
                const isApplying = applyingJobId === job._id;
                return (
                  <tr key={job._id}>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {new Date(job.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">{job.originalFileName}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{job.pricingScope?.mode}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      <span className="px-2 py-1 rounded bg-gray-100">{job.status}</span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {job.validationSummary
                        ? `${job.validationSummary.validRows || 0} valid / ${job.validationSummary.invalidRows || 0} invalid`
                        : 'Not validated'}
                    </td>
                    <td className="px-4 py-4 text-sm text-right space-x-2">
                      <button
                        type="button"
                        onClick={() => handleValidate(job)}
                        disabled={!canValidateJob(job)}
                        className="inline-flex items-center text-indigo-600 hover:text-indigo-900 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <FiCheckCircle className="mr-1" />
                        Validate
                      </button>
                      <button
                        type="button"
                        onClick={() => handleApply(job)}
                        disabled={!canApplyJob(job) || isApplying}
                        className="inline-flex items-center text-green-600 hover:text-green-900 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <FiUpload className="mr-1" />
                        {isApplying ? 'Applying...' : 'Apply'}
                      </button>
                      {job.validationReportFilePath && (
                        <button
                          type="button"
                          onClick={() => handleDownloadValidationReport(job)}
                          className="inline-flex items-center text-blue-600 hover:text-blue-900"
                        >
                          <FiDownload className="mr-1" />
                          Report
                        </button>
                      )}
                      {((job.status === 'applied') || (job.status === 'failed' && job.hasRollbackData)) && (
                        <button
                          type="button"
                          onClick={() => handleRollback(job)}
                          className="inline-flex items-center text-red-600 hover:text-red-900"
                        >
                          <FiRotateCcw className="mr-1" />
                          Rollback
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductBulkUpdatesPage;
